<script setup lang="ts">
import { computed } from 'vue';
import { useLocalization } from '@/plugins/localization';
import { KeyValuePair } from '@/helpers/Interfaces';
import { AttributesModel, Column } from '@/modules/core/common/services/GridService';
import { getAttributes } from '@/components/common/dynamic-grid/helpers/TypeOfData';

// Presenters
import DateTimePresenter from '../presenters/DateTime.vue';
import UserWithAvatarPresenter from '../presenters/UserWithAvatar.vue';
import UserWithAvatarArrayPresenter from '../presenters/UserWithAvatarArray.vue';
import TextPresenter from '../presenters/Text.vue';
import StatusPresenter from '../presenters/StatusWithVariant.vue';
import MoneyPresenter from '../presenters/Money.vue';
import BooleanPresenter from '../presenters/Boolean.vue';
import GenericKeyValuePairPresenter from '../presenters/GenericKeyValuePair.vue';
import GenericKeyValuePairArrayPresenter from '../presenters/GenericKeyValuePairArray.vue';
import DecimalPresenter from '../presenters/Decimal.vue';
import AddressPresenter from '../presenters/Address.vue';
import MainFilePresenter from '../presenters/File.vue';
import ListPresenter from '../presenters/List.vue';
import SimplePartnerPresenter from '../presenters/SimplePartner.vue';
import SimplePartnerArrayPresenter from '../presenters/SimplePartnerArray.vue';
import DurationPresenter from '../presenters/Duration.vue';
import ResourcePresenter from '../presenters/Resource.vue';
import TableVatPresenter from '../presenters/TableVat.vue';
import UserGroupArrayPresenter from '../presenters/UserGroupArray.vue';
import ExchangeRatePresenter from '../presenters/ExchangeRate.vue';
import DynamicDocumentPresenter from '../presenters/DynamicDocument.vue';
import IsPrivatePresenter from '../presenters/IsPrivate.vue';
import RichTextPresenter from '../presenters/RichText.vue';
import ParticipantList from '@/modules/low-code/components/ParticipantList.vue';

interface Props {
    property: Record<string, any>[]
    features: KeyValuePair[]
    tableSchema: { head: Column; body: { [key: string]: Column } }
    isOnGrid?: boolean
    canCopy?: boolean
    columnsVisibility?: Record<string, boolean>
    customConfig?: any
}

const props = defineProps({
  "property": null,
  "features": null,
  "tableSchema": { default: () => ({ head: {} as Column, body: {} }) },
  "isOnGrid": { type: Boolean, default: false },
  "canCopy": { type: Boolean, default: false },
  "columnsVisibility": { default: () => ({}) },
  "customConfig": { default: null }
});

const componentMap = {
    'DateTimePresenter': DateTimePresenter,
    'UserWithAvatarPresenter': UserWithAvatarPresenter,
    'UserWithAvatarArrayPresenter': UserWithAvatarArrayPresenter,
    'TextPresenter': TextPresenter,
    'StatusPresenter': StatusPresenter,
    'MoneyPresenter': MoneyPresenter,
    'BooleanPresenter': BooleanPresenter,
    'GenericKeyValuePairPresenter': GenericKeyValuePairPresenter,
    'GenericKeyValuePairArrayPresenter': GenericKeyValuePairArrayPresenter,
    'DecimalPresenter': DecimalPresenter,
    'AddressPresenter': AddressPresenter,
    'MainFilePresenter': MainFilePresenter,
    'ListPresenter': ListPresenter,
    'SimplePartnerPresenter': SimplePartnerPresenter,
    'SimplePartnerArrayPresenter': SimplePartnerArrayPresenter,
    'DurationPresenter': DurationPresenter,
    'ResourcePresenter': ResourcePresenter,
    'TableVatPresenter': TableVatPresenter,
    'UserGroupArrayPresenter': UserGroupArrayPresenter,
    'ExchangeRatePresenter': ExchangeRatePresenter,
    'DynamicDocumentPresenter': DynamicDocumentPresenter,
    'IsPrivatePresenter': IsPrivatePresenter,
    'RichTextPresenter': RichTextPresenter,
    'ParticipantList': ParticipantList
};

const { $i18n } = useLocalization();

const isFooter = computed((): boolean =>
{
    return props.tableSchema.head.columns?.some((item: { columnName: Record<string, string>; fieldName: string; summary: boolean; formula: string }) => item.summary) || false;
});

const hasData = computed((): boolean =>
{
    return props.property && Array.isArray(props.property) && props.property.length > 0;
});

function translatedText(text: Record<string, string>): string
{
    return $i18n.currentTranslation(text);
}

function TypeOfData(value: any, header: Column): Record<string, any>
{
    const headerOptions = header;
    const property = value;
    const features = headerOptions.type.features;
    const attributes: AttributesModel = {
        property,
        features,
        ...getAttributes(headerOptions.type.baseType, {
            isInTable: true,
            canCopy: props.canCopy,
            customConfig: props.customConfig
        })
    };

    return attributes;
}

function getSumOfColumn(colName: string): number
{
    if (!props.property) return 0;

    const column = props.tableSchema.body[colName];

    if (!column) return 0;

    if (column.type.baseType !== 'Decimal') return 0;

    return props.property.reduce((acc: number, item: any) =>
    {
        const value = Number(item[colName]);

        return acc + (isNaN(value) ? 0 : value);
    }, 0);
}

function colWidth(index: number): any
{
    const minWidth = 50;
    const maxWidth = 2000;
    let width = minWidth;

    if (props.customConfig != null && props.customConfig?.columns != null && props.customConfig?.columns.length > index)
    {
        const columnWidth = parseInt(props.customConfig?.columns[index]?.width ?? 0);

        if (columnWidth != null && !isNaN(columnWidth))
        {
            width = columnWidth;
        }
    }

    function clamp(val: number, min: number, max: number): number
    {
        return val > max ? max : val < min ? min : val;
    }

    return {
        ...(width ? { width: `${clamp(width, minWidth, maxWidth)}px` } : {})
    };
}

function getComponent(componentName: string)
{
    return componentMap[componentName as keyof typeof componentMap] || 'div';
}
</script>

<template>
    <div class="scroll scroll--overflow-y-hidden">
        <span v-if="!hasData">-</span>
        <table v-else class="table table-bordered mb-2">
            <thead v-if="tableSchema.head?.columns">
                <tr>
                    <th
                        v-for="(col, index) in tableSchema.head.columns"
                        v-show="columnsVisibility?.[col.fieldName] != false"
                        :key="index"
                        :style="colWidth(index)"
                    >
                        {{ translatedText(col.columnName) }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(row, indexRow) in property" :key="indexRow">
                    <td
                        v-for="(col, indexCol) in tableSchema.head.columns"
                        v-show="columnsVisibility?.[col.fieldName] != false"
                        :key="indexCol"
                        class="p-2"
                    >
                        <component
                            :is="getComponent(TypeOfData(row[col.fieldName], tableSchema.body[col.fieldName]).is)"
                            v-bind="{ ...TypeOfData(row[col.fieldName], tableSchema.body[col.fieldName]) }"
                        />
                    </td>
                </tr>
            </tbody>
            <tfoot v-if="isFooter">
                <tr class="border-0">
                    <td
                        v-for="(col, index) in tableSchema.head.columns"
                        v-show="columnsVisibility?.[col.fieldName] != false"
                        :key="index"
                        :class="!col.summary ? 'border-0' : 'table--border'"
                    >
                        <span v-if="col.summary" class="fw-bold">
                            <component
                                :is="getComponent(TypeOfData(getSumOfColumn(col.fieldName), tableSchema.body[col.fieldName]).is)"
                                v-bind="{
                                    ...TypeOfData(getSumOfColumn(col.fieldName), tableSchema.body[col.fieldName]),
                                }"
                            />
                        </span>
                    </td>
                </tr>
            </tfoot>
        </table>
    </div>
</template>

<style lang="scss" scoped>
.table {
    table-layout: fixed;
    width: 100%;

    thead {
        tr {
            height: 36.5px;
            background: var(--border-color-nav);

            th {
                width: 130px;
                padding: 0.5rem;
            }
        }
    }

    tbody {
        tr {
            td {
                padding: 0.5rem;
                word-break: break-word;
                white-space: normal;

                :deep(span) {
                    white-space: normal !important;
                    word-break: break-word;
                    display: inline-block;
                    width: 100%;
                }
            }
        }
    }

    &--border {
        background: var(--border-color-nav);
        border: 1px solid var(--bs-table-border-color);
    }
}
</style>
